'use client';

import { useState, type CSSProperties, type FormEvent } from 'react';
import { CATEGORY_LABELS, GRIEVANCE_CATEGORIES, LIMITS } from '@/lib/grievances';

interface ProjectOption { id: string; name: string; projectCode: string | null }

export function GrievanceForm({ projects }: { projects: ProjectOption[] }) {
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [reference, setReference] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ category: 'ENVIRONMENTAL', projectId: '', subject: '', description: '', anonymous: false, contactName: '', contactEmail: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }
  async function submit() {
    setState('sending'); setMessage('');
    try {
      const res = await fetch('/api/grievances', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const body = (await res.json().catch(() => ({}))) as { error?: string; reference?: string };
      if (!res.ok) { setState('error'); setMessage(body.error || `Not sent (${res.status}).`); return; }
      setReference(body.reference ?? '');
      setState('done');
    } catch {
      setState('error'); setMessage('Network error — the grievance was not sent.');
    }
  }

  if (state === 'done') {
    return (
      <div role="status" style={{ ...box, borderColor: 'var(--mcc-sea)' }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: 6 }}>Grievance recorded — reference {reference}</div>
        <p style={para}>Keep this reference. It appears in the public register below with its status. {form.anonymous ? 'You filed anonymously, so the programme cannot write to you; follow the reference here.' : 'The programme will acknowledge in writing within 30 days to the contact you gave.'}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} style={box}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--mcc-navy)', margin: '0 0 6px' }}>File a grievance</h2>
      <p style={{ ...para, color: 'var(--mcc-muted)', marginBottom: 16 }}>
        Free of charge, open to anyone. You may file anonymously; anonymous grievances are processed with the same rigour (ESS Policy cl. 0109). No retaliation is permitted against anyone who files (cl. 0123).
      </p>
      {state === 'error' && <div role="alert" style={{ ...notice, background: 'var(--mcc-danger-bg, #f7e2e0)', color: '#842029' }}>{message}</div>}

      <div style={grid2}>
        <label style={label}>What is it about *
          <select value={form.category} onChange={set('category')} style={input}>
            {GRIEVANCE_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
          </select>
        </label>
        <label style={label}>Project (if any)
          <select value={form.projectId} onChange={set('projectId')} style={input}>
            <option value="">— programme-level / not project-specific —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.projectCode ? `${p.projectCode} — ` : ''}{p.name}</option>)}
          </select>
        </label>
      </div>
      <label style={label}>Subject *
        <input required maxLength={LIMITS.subject} value={form.subject} onChange={set('subject')} style={input} />
      </label>
      <label style={{ ...label, marginTop: 12 }}>What happened, where and when *
        <textarea required rows={7} minLength={20} maxLength={LIMITS.description} value={form.description} onChange={set('description')} style={{ ...input, resize: 'vertical' }} />
        <span style={{ fontSize: 12, color: 'var(--mcc-muted)', fontWeight: 400 }}>{form.description.length} / {LIMITS.description}</span>
      </label>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '14px 0', fontSize: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={form.anonymous} onChange={(e) => setForm((f) => ({ ...f, anonymous: e.target.checked }))} />
        File anonymously
      </label>
      {!form.anonymous && (
        <div style={grid2}>
          <label style={label}>Your name
            <input maxLength={LIMITS.contactName} value={form.contactName} onChange={set('contactName')} style={input} autoComplete="name" />
          </label>
          <label style={label}>Email for the written acknowledgement
            <input type="email" maxLength={LIMITS.contactEmail} value={form.contactEmail} onChange={set('contactEmail')} style={input} autoComplete="email" />
          </label>
        </div>
      )}
      <p style={{ ...para, fontSize: 13, color: 'var(--mcc-muted)', margin: '10px 0 16px' }}>
        Your name, email and the description are seen by programme staff only. The public register shows the reference, category, project and status.
      </p>
      <button type="submit" disabled={state === 'sending'} style={button}>{state === 'sending' ? 'Sending…' : 'File grievance'}</button>
    </form>
  );
}

const box: CSSProperties = { background: 'white', border: '1px solid var(--mcc-border)', borderRadius: 8, padding: 24 };
const para: CSSProperties = { fontSize: 15, lineHeight: 1.6, margin: 0 };
const grid2: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 };
const label: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: 'var(--mcc-muted)' };
const input: CSSProperties = { padding: '10px 12px', border: '1px solid var(--mcc-border)', borderRadius: 4, fontSize: 15, color: 'var(--mcc-ink)', background: 'white', width: '100%', fontFamily: 'inherit' };
const notice: CSSProperties = { padding: '10px 14px', borderRadius: 6, fontSize: 14, marginBottom: 16 };
const button: CSSProperties = { background: 'var(--mcc-navy)', color: 'white', border: 'none', borderRadius: 4, padding: '12px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer' };
